import { useState, useEffect } from 'react';
import { CalendarCheck, Gift, Loader2, CheckCircle2 } from 'lucide-react';
import { checkinAPI } from '../services/api';

interface CheckInButtonProps {
  onCheckIn?: (credits: number) => void;
  compact?: boolean;
}

export default function CheckInButton({ onCheckIn, compact = false }: CheckInButtonProps) {
  const [config, setConfig] = useState<{ enabled: boolean; minCredits: number; maxCredits: number } | null>(null);
  const [status, setStatus] = useState<{ checkedInToday: boolean; todayCredits: number | null; consecutiveDays: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCheckInData();
  }, []);

  const loadCheckInData = async () => {
    try {
      setChecking(true);
      const [configData, statusData] = await Promise.all([
        checkinAPI.getConfig(),
        checkinAPI.getStatus(),
      ]);
      setConfig(configData);
      setStatus(statusData);
    } catch (err) {
      console.error('加载签到数据失败:', err);
    } finally {
      setChecking(false);
    }
  };

  const handleCheckIn = async () => {
    if (!config?.enabled || status?.checkedInToday) return;

    try {
      setLoading(true);
      setError(null);
      const result = await checkinAPI.checkIn();
      setStatus(prev => prev ? {
        ...prev,
        checkedInToday: true,
        todayCredits: result.credits,
        consecutiveDays: prev.consecutiveDays + 1,
      } : null);
      onCheckIn?.(result.credits);
    } catch (err) {
      setError(err instanceof Error ? err.message : '签到失败');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    if (compact) {
      return (
        <button disabled className="btn-secondary text-sm flex items-center gap-1 opacity-60">
          <Loader2 className="w-4 h-4 animate-spin" />
          签到
        </button>
      );
    }
    return (
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-4">
        <div className="flex items-center justify-center h-16">
          <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (!config?.enabled) {
    return null;
  }

  if (compact) {
    return (
      <button
        onClick={handleCheckIn}
        disabled={status?.checkedInToday || loading}
        className={`
          btn-secondary text-sm flex items-center gap-1 transition-all duration-200
          ${status?.checkedInToday
            ? 'text-green-600 border-green-300 bg-green-50'
            : 'text-orange-600 border-orange-300 bg-orange-50 hover:bg-orange-100'
          }
          disabled:opacity-70 disabled:cursor-not-allowed
        `}
        title={status?.checkedInToday ? `今日已签到，获得 ${status.todayCredits} 积分` : `签到领 ${config.minCredits}-${config.maxCredits} 积分`}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : status?.checkedInToday ? (
          <CheckCircle2 className="w-4 h-4" />
        ) : (
          <CalendarCheck className="w-4 h-4" />
        )}
        {status?.checkedInToday ? '已签到' : '签到'}
      </button>
    );
  }

  return (
    <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-200">
            <CalendarCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">每日签到</h3>
            <p className="text-sm text-gray-600">
              {status?.checkedInToday ? (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle2 className="w-4 h-4" />
                  今日已签到，获得 {status.todayCredits} 积分
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <Gift className="w-4 h-4 text-orange-500" />
                  签到领 {config.minCredits}-{config.maxCredits} 积分
                </span>
              )}
            </p>
            {status && status.consecutiveDays > 0 && (
              <p className="text-xs text-orange-600 mt-0.5">
                已连续签到 {status.consecutiveDays} 天
              </p>
            )}
          </div>
        </div>

        <button
          onClick={handleCheckIn}
          disabled={status?.checkedInToday || loading}
          className={`
            px-5 py-2.5 rounded-xl font-medium transition-all duration-200
            ${status?.checkedInToday
              ? 'bg-green-100 text-green-700 cursor-default'
              : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-200 hover:shadow-xl hover:scale-105 active:scale-95'
            }
            disabled:opacity-70 disabled:cursor-not-allowed
          `}
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : status?.checkedInToday ? (
            '已签到'
          ) : (
            '签到'
          )}
        </button>
      </div>

      {error && (
        <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
}
